import React, { useState, useEffect } from "react";
import UserCard from "../components/UserCard";
import AdminPanelSystems from "../components/AdminPanelSystem";
import UserFormModal from "../components/UserFormModal";
import "./AdminPanel.css";

import { showCustomToast } from "../components/CustomToast";
import BillingSection from "../components/BillingSection";
import AddSystemForm from "./AddSystemForm"; // Import your payment form
import { Modal, Button } from "react-bootstrap";
import { getUsersInOrg } from "../services/api/getUsersInOrg";
import { createNewUser } from "../services/api/createNewUser";
import { getOrgSystems } from "../services/api/getOrgSystems";
import { getOrganizationSettings, updateOrganizationSettings, OrganizationSettings } from "../services/api/organizationSettings";
import { useSocket } from "../components/Contexts/SocketContext";

interface User {
  sub: string;
  username: string;
  fullname: string;
  phonenumber: string;
  role: string;
  // NULL/undefined = active; ISO timestamp = soft-disabled at that time.
  // Disabled users keep their row so audit-trail FKs (check_logs,
  // live_view_sessions, audit_logs) stay intact; backend gates auth on
  // this in both the HTTP middleware and the socket handshake.
  disabled_at?: string | null;
}

interface System {
  id: number;
  system_name: string;
  status: string;
  guid: string;
  version?: string;
}

type NotifyType = "success" | "error" | "warning" | "info";
const NOTIFY_TITLES: Record<NotifyType, string> = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
};
// Admin-panel feedback goes through the app-wide toast so it matches the rest
// of the UI and stays pinned to the viewport regardless of scroll position.
const notify = (message: string, type: NotifyType) =>
  showCustomToast(NOTIFY_TITLES[type], message, type);

const AdminPanel = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddSystemModal, setShowAddSystemModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);

  // Organization settings state
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings>({
    check_reminder_hours: 0,
    check_reminder_minutes: 45,
    send_missed_checks_to_admins: true,
    send_text_reminders: false,
    max_systems_per_user: 12
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [installerLoading, setInstallerLoading] = useState(false);

  // Org-wide announcement modal (admin-only ephemeral toast to everyone).
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementText, setAnnouncementText] = useState("");
  const { socket } = useSocket();

  const sendAnnouncement = () => {
    if (!socket) return;
    const trimmed = announcementText.trim();
    if (!trimmed) return;
    socket.emit("admin_broadcast", { message: trimmed });
    setShowAnnouncementModal(false);
    setAnnouncementText("");
  };

  const downloadInstaller = async () => {
    setInstallerLoading(true);
    try {
      const res = await fetch("/api/v1/installer-info", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { downloadUrl, version, filename } = await res.json();
      // Trigger a real download via a temporary anchor — browser handles it
      // without leaving the admin page. The S3 URL serves the binary with
      // Content-Disposition: attachment so the browser saves it.
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename || `EpicareAlert-${version}.exe`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      notify(`Downloading EpicareAlert ${version}`, "info");
    } catch (e) {
      console.error("downloadInstaller failed", e);
      notify("Could not fetch installer — try again later", "error");
    } finally {
      setInstallerLoading(false);
    }
  };

  useEffect(() => {
    // Fetch users, systems, and licenses...
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch users
        const users = await getUsersInOrg();
        if (users) {
          // Admins and superadmins sort first (both are admin-or-higher).
          const isAdminish = (r: string) => r === "admin" || r === "superadmin";
          const sortedUsers = users.sort((a: User, b: User) => {
            if (isAdminish(a.role) && !isAdminish(b.role)) return -1;
            if (!isAdminish(a.role) && isAdminish(b.role)) return 1;
            return 0;
          });
          setUsers(sortedUsers);
        }

        // Fetch systems from the backend
        const fetchedSystems = await getOrgSystems();
        const getNum = (s: System) => parseInt(s.system_name?.match(/(\d+)\s*$/)?.[1] ?? "0");
        setSystems([...fetchedSystems].sort((a, b) => getNum(a) - getNum(b)));
      } catch (error) {
        console.error("Error fetching data:", error);
        notify("Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchOrgSettings();
  }, []);

  const fetchOrgSettings = async () => {
    try {
      const settings = await getOrganizationSettings();
      setOrgSettings(settings);
    } catch (error) {
      console.error('Error fetching organization settings:', error);
    }
  };

  const updateOrgSettings = async () => {
    setSettingsLoading(true);
    try {
      await updateOrganizationSettings(orgSettings);
      notify('Settings updated successfully', "success");
    } catch (error) {
      console.error('Error updating settings:', error);
      notify('Failed to update settings', "error");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleAddUser = () => {
    setShowUserModal(true);
  };

  const handleSaveUser = async (newUser: {
    fullname: string;
    username: string;
    phonenumber: string;
    password: string;
    role: string;
  }) => {
    try {
      const response = await createNewUser(
        newUser.fullname,
        newUser.username,
        newUser.phonenumber,
        newUser.password,
        newUser.role
      );
      if (response?.message) {
        // Handle the 'User account already exists' error
        notify(response.message, "error");
        return;
      } else {
        setUsers([...users, response]);
        setShowUserModal(false);
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      notify("Failed to create user", "error");
      setShowUserModal(true);
    }
  };

  const handleEditUser = (id: string) => {
    console.log(`Edit user with ID: ${id}`);
  };

  // Hard-delete — only for users created by mistake. The backend refuses
  // unless the user has ZERO activity (checks, logins, live views,
  // claimed systems), so a real operator can't be deleted this way even
  // if this UI misfires; Disable is the tool for them. The admin must
  // re-type the user's email, and the backend independently verifies the
  // re-typed value before deleting from Cognito and the DB.
  const handleDeleteUser = async (id: string) => {
    const user = users.find((u) => u.sub === id);
    if (!user) return;
    const typed = window.prompt(
      `PERMANENTLY delete ${user.fullname}?\n\n` +
      `This cannot be undone. It only works for users with no activity ` +
      `history — for anyone who has actually used the system, use Disable ` +
      `instead.\n\n` +
      `To confirm, type the user's email exactly:\n${user.username}`,
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== user.username) {
      notify("Email did not match — deletion cancelled.", "warning");
      return;
    }
    try {
      const res = await fetch("/api/v1/admin/delete-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userSub: id, confirmUsername: typed.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setUsers((prev) => prev.filter((u) => u.sub !== id));
        notify(`Deleted ${user.fullname} (${user.username}).`, "success");
      } else {
        notify(`Delete failed: ${data.message || res.statusText}`, "error");
      }
    } catch (err: any) {
      notify(`Delete failed: ${err.message || "Network error"}`, "error");
    }
  };

  // Reset MFA = delete + recreate the user in Cognito (only way to clear
  // a TOTP token registration — Cognito has no admin API for it). Backend
  // does the dance atomically: delete Cognito user, create new one,
  // rewrite all DB sub references in a transaction. User then uses
  // Forgot Password to set a new password and re-enrolls their
  // authenticator app on next login.
  const handleResetMfa = async (id: string, fullName: string) => {
    if (!window.confirm(
      `Reset MFA for ${fullName}?\n\n` +
      `This deletes and recreates the user in Cognito. The user will need to:\n` +
      `  1. Click "Forgot Password" on the login page to set a new password\n` +
      `  2. Sign in with the new password\n` +
      `  3. Scan a new QR code with their authenticator app\n\n` +
      `Their old authenticator app entry will stop working. ` +
      `All check history and login history is preserved.`,
    )) return;
    try {
      const res = await fetch("/api/v1/admin/reset-mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userSub: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        notify(
          `MFA reset for ${fullName}. Tell them to use "Forgot Password" to set a new password and re-enroll their authenticator on next login.`,
          "success",
        );
      } else {
        notify(`Reset MFA failed: ${data.message || res.statusText}`, "error");
      }
    } catch (err: any) {
      notify(`Reset MFA failed: ${err.message || "Network error"}`, "error");
    }
  };

  // Soft-disable / re-enable a user. Used for terminations and leaves —
  // preferable to deleting because the user's history (check_logs,
  // live_view_sessions, audit_logs) stays intact and recoverable if
  // they ever come back. Backend disables them in Cognito, marks
  // disabled_at, and kicks any active sockets.
  const handleToggleUserDisabled = async (id: string, fullName: string, currentlyDisabled: boolean) => {
    const verb = currentlyDisabled ? "re-enable" : "disable";
    const Verb = currentlyDisabled ? "Re-enable" : "Disable";
    if (currentlyDisabled) {
      if (!window.confirm(`Re-enable ${fullName}?\n\nThey'll be able to sign in again on their next login.`)) return;
    } else {
      if (!window.confirm(
        `Disable ${fullName}?\n\n` +
        `• They will be immediately signed out of any open sessions\n` +
        `• They will not be able to sign in again until re-enabled\n` +
        `• Their check history and login history is preserved\n\n` +
        `Use this for terminations or extended leaves of absence.`,
      )) return;
    }
    try {
      const endpoint = currentlyDisabled ? "/api/v1/admin/enable-user" : "/api/v1/admin/disable-user";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userSub: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setUsers((prev) =>
          prev.map((u) =>
            u.sub === id
              ? { ...u, disabled_at: currentlyDisabled ? null : new Date().toISOString() }
              : u,
          ),
        );
        notify(`${Verb}d ${fullName}.`, "success");
      } else {
        notify(`Failed to ${verb}: ${data.message || res.statusText}`, "error");
      }
    } catch (err: any) {
      notify(`Failed to ${verb}: ${err.message || "Network error"}`, "error");
    }
  };

  const handleDeleteSystem = (systemId: number) => {
    console.log(`Delete system with ID: ${systemId}`);
  };
  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(33, 150, 243, 0.1)',
        borderRadius: '16px',
        padding: '32px',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          fontSize: '32px',
          color: '#2196F3',
          marginBottom: '16px'
        }}>⚡</div>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#1e293b'
        }}>Loading Admin Panel...</div>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      <BillingSection />

      {/* Organization Settings Section */}
      <div className="section-header-container">
        <h2 className="section-header">Organization Settings</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="add-user-button"
            style={{ background: "#8b5cf6" }}
            onClick={() => setShowAnnouncementModal(true)}
            title="Send a toast notification to all currently-connected users in this organization"
          >
            Send Announcement
          </button>
          <button
            className="add-user-button"
            onClick={updateOrgSettings}
            disabled={settingsLoading}
          >
            {settingsLoading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
      <div className="org-settings-container" style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: '600', color: '#374151' }}>Check Reminder Time:</label>
            <input
              type="number"
              min="1"
              max="59"
              value={orgSettings.check_reminder_minutes}
              onChange={(e) => setOrgSettings({
                ...orgSettings,
                check_reminder_hours: 0,
                check_reminder_minutes: Math.max(1, Math.min(59, parseInt(e.target.value) || 1)),
              })}
              style={{
                width: '70px',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                textAlign: 'center'
              }}
            />
            <span style={{ color: '#6b7280' }}>minutes after last check</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="send-missed-checks"
              checked={orgSettings.send_missed_checks_to_admins}
              onChange={(e) => setOrgSettings({...orgSettings, send_missed_checks_to_admins: e.target.checked})}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="send-missed-checks" style={{ fontWeight: '500', color: '#374151' }}>
              Send missed checks to admins
            </label>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="send-text-reminders"
              checked={orgSettings.send_text_reminders}
              onChange={(e) => setOrgSettings({...orgSettings, send_text_reminders: e.target.checked})}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="send-text-reminders" style={{ fontWeight: '500', color: '#374151' }}>
              Send text reminders
            </label>
          </div>
        </div>
      </div>

      {/* Max systems per user — own card so the limit setting is visually
          distinct from the reminder/notification toggles above. */}
      <div className="org-settings-container" style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label htmlFor="max-systems-per-user" style={{ fontWeight: '600', color: '#374151' }}>
            Max systems per user:
          </label>
          <input
            id="max-systems-per-user"
            type="number"
            min="1"
            max="1000"
            value={orgSettings.max_systems_per_user}
            onChange={(e) => setOrgSettings({
              ...orgSettings,
              max_systems_per_user: Math.max(1, Math.min(1000, parseInt(e.target.value) || 1))
            })}
            style={{
              width: '80px',
              padding: '8px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              textAlign: 'center'
            }}
            title="Caps how many systems a non-admin user can have claimed at once. Admins are unaffected."
          />
          <span style={{ color: '#6b7280', fontSize: '13px' }}>
            Caps how many systems a non-admin user can claim at once. Admins are unaffected.
          </span>
        </div>
      </div>

      {/* Users Section */}
      <div className="section-header-container">
        <h2 className="section-header">Users</h2>
        <button className="add-user-button" onClick={handleAddUser}>
          Add User
        </button>
      </div>
      <div className="user-grid">
        {users.length > 0 &&
          users.map((user) => (
            <UserCard
              key={user.sub}
              id={user.sub}
              fullName={user.fullname}
              username={user.username}
              phoneNumber={user.phonenumber}
              role={user.role}
              disabled={!!user.disabled_at}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onResetMfa={handleResetMfa}
              onToggleDisabled={handleToggleUserDisabled}
            />
          ))}
      </div>

      {/* Systems Section */}
      <div className="section-header-container">
        <h2 className="section-header">Systems</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="add-system-button"
            style={{ background: "#0ea5e9" }}
            onClick={downloadInstaller}
            disabled={installerLoading}
            title="Downloads the latest EpicareAlert Windows installer (.exe) from S3"
          >
            {installerLoading ? "Fetching..." : "Download Installer"}
          </button>
          <button
            className="add-system-button"
            style={{ background: "#f59e0b" }}
            onClick={async () => {
              const entered = window.prompt("Enter the force-update PIN to push an update to ALL systems:");
              if (entered === null) return; // user cancelled
              if (entered !== "5381") {
                notify("Incorrect PIN. Force update aborted.", "error");
                return;
              }
              const res = await fetch("/api/v1/forceUpdate", { method: "POST", credentials: "include" });
              const data = await res.json();
              notify(data.message, res.ok ? "success" : "error");
            }}
          >
            Force Update All
          </button>
          <button
            className="add-system-button"
            onClick={() => setShowAddSystemModal(true)}
          >
            Add System
          </button>
        </div>
      </div>
      <AdminPanelSystems
        systems={systems.map((system) => ({
          id: system.id,
          system_name: system.system_name,
          status: system.status,
          guid: system.guid,
          version: system.version,
          onDelete: handleDeleteSystem,
        }))}
        onDeactivate={async (systemId) => {
          try {
            const res = await fetch("/api/v1/deactivateSystem", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ systemId }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || `HTTP ${res.status}`);
            }
            notify(`System deactivated. The app on that machine has been kicked.`, "success");
            // The "system_removed" socket event from the server will clean
            // up the dashboard automatically via SystemsContext; nothing to
            // do here except acknowledge.
          } catch (e: any) {
            notify(`Failed to deactivate: ${e.message || e}`, "error");
            throw e;
          }
        }}
      />

      {/* User Form Modal */}
      <UserFormModal
        show={showUserModal}
        handleClose={() => setShowUserModal(false)}
        handleSave={handleSaveUser}
      />

      {/* Announcement Modal — admin broadcasts ephemeral toast to whoever is
          currently connected in the org. No persistence, no replay. */}
      <Modal
        show={showAnnouncementModal}
        onHide={() => setShowAnnouncementModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "10px" }}>
            All techs currently logged in will see this as a toast notification.
            Users not connected when you send this will miss it.
          </div>
          <textarea
            value={announcementText}
            onChange={(e) => setAnnouncementText(e.target.value.slice(0, 500))}
            placeholder="Type your announcement..."
            autoFocus
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontFamily: "inherit",
              fontSize: "14px",
              resize: "vertical",
            }}
          />
          <div style={{ fontSize: "12px", color: "#9ca3af", textAlign: "right", marginTop: "4px" }}>
            {announcementText.length} / 500
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAnnouncementModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={sendAnnouncement}
            disabled={!announcementText.trim()}
            style={{ background: "#8b5cf6", border: "none" }}
          >
            Send to Org
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add System Form Modal */}
      <Modal
        show={showAddSystemModal}
        onHide={() => setShowAddSystemModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add System</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddSystemForm
            handleSuccess={(system: System) => {
              setSystems([...systems, system]);
              setShowAddSystemModal(false);
            }}
            handleError={(error: any) => {
              console.error("Error adding system:", error);
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminPanel;
